import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { escapeCloses } from './escape';
import { focusRing } from './focus';

export const TOAST_MS = 2500;
/** More toasts at once than this and the oldest leaves (P15c): they stack instead of covering each other. */
export const TOAST_MAX_VISIBLE = 3;

/** One stack for every toast of the page, at the bottom centre, newest at the bottom. */
function toastStack(): HTMLElement {
  let el = document.querySelector<HTMLElement>('[data-toasts]');
  if (!el) {
    el = document.createElement('div');
    el.dataset.toasts = '';
    el.className = 'pointer-events-none fixed inset-x-0 bottom-20 z-40 flex flex-col items-center gap-2 px-4';
    document.body.append(el);
  }
  return el;
}

/** The toasts on screen, oldest first: a new one past TOAST_MAX_VISIBLE closes the oldest. */
const live: { done: () => void }[] = [];

type ToastProps = {
  message: string;
  onDone: () => void;
  /** A button in the toast ("Undo", P16-4): the toast then takes pointer events and closes on Escape. */
  action?: { label: string; onClick: () => void };
  duration?: number;
  /** Rendered where it stands, outside the page's stack and its limit: the catalogue's previews only. */
  inline?: boolean;
};

/**
 * A short message at the bottom of the screen, announced politely to screen readers, gone after 2.5 s. Every toast goes
 * to one stack (two at once used to sit on top of each other), at most TOAST_MAX_VISIBLE of them.
 */
export function Toast({ message, onDone, action, duration = TOAST_MS, inline = false }: ToastProps) {
  const ref = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  // The toast never takes the focus; when someone tabs into it, the focus goes back where it came from on close.
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (inline) return;
    const entry = { done: () => done.current() };
    live.push(entry);
    if (live.length > TOAST_MAX_VISIBLE) live.shift()!.done();
    return () => {
      const i = live.indexOf(entry);
      if (i >= 0) live.splice(i, 1);
    };
  }, [inline]);

  useEffect(() => {
    const timer = setTimeout(() => done.current(), duration);
    return () => clearTimeout(timer);
  }, [message, duration]);

  useEffect(() => {
    if (!action) return;
    const onKey = (e: KeyboardEvent) => {
      if (escapeCloses(e, ref.current)) done.current();
    };
    document.addEventListener('keydown', onKey);
    const node = ref.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      const active = document.activeElement;
      const back = returnTo.current;
      if (back?.isConnected && (!active || active === document.body || node?.contains(active))) back.focus();
    };
  }, [action]);

  const toast = (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      onFocus={(e) => {
        if (e.relatedTarget instanceof HTMLElement && !ref.current?.contains(e.relatedTarget)) returnTo.current = e.relatedTarget;
      }}
      className={cn(
        action ? 'pointer-events-auto' : 'pointer-events-none',
        'os-enter flex items-center gap-3 rounded-full bg-fg px-4 py-2 font-sans text-sm text-bg shadow-float',
      )}
    >
      {message}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className={cn('rounded-full px-2.5 py-1 font-medium text-bg underline-offset-2 hover:underline pointer-coarse:min-h-11', focusRing)}
        >
          {action.label}
        </button>
      )}
    </div>
  );
  return inline ? toast : createPortal(toast, toastStack());
}
